/**
 * Node ownership transfer (app#376, router `node_transfer`, router#1771; docs/PERMISSIONS.md § 5
 * Node: "Transfer ownership to another member of the node's org — node owner or Admin … The result
 * lists every root delegation the new owner inherits").
 *
 * Pure, so `nodeTransfer.test.ts` checks it with `node --experimental-strip-types`.
 */

import { userText } from './userLabel.ts'

/** One member of the node's org, as `org_members_list` returns it (camelcased). */
export interface TransferMember {
  userId: string
  email: string
  name: string
  role: string
}

/** One root delegation the new owner inherits, as `node_transfer` returns it. */
export interface RootDelegation {
  kind: string
  service_id?: string
  name?: string
  title?: string
  user_id?: string
  email?: string
  /** A `root_entry` that stops counting at this time (RFC3339 UTC). */
  expires_at?: string
  /** A `root_entry` while the node does not allow root: kept, but it gives only `use`. */
  blocked_by_node?: boolean
}

/** The `node_transfer` payload (`callTool` has already unwrapped it). */
export interface NodeTransferResult {
  node_id: string
  name: string
  owner_id: string
  previous_owner_id?: string
  local_mcp_turned_off?: boolean
  root_delegations?: RootDelegation[] | null
}

/**
 * `node_transfer` arguments. ⚠ `node_id`, never a name: the flat node reference the router takes.
 * ⚠⚠ THE NEW OWNER IS NAMED BY EMAIL (docs/PERMISSIONS.md § 5 Node, D-16), never by `user_id`. A
 * Member who owns a node cannot list the org's members (router#1765), but can type an address. The
 * router refuses an address that is not a member of the node's org, and does not say why.
 */
export function nodeTransferArgs(nodeId: string, email: string): Record<string, unknown> {
  return { node_id: nodeId, email: email.trim() }
}

/**
 * Whether the call gave the node to someone else. ⚠ The router answers SUCCESS, with no change,
 * when the address is the node's current owner: `owner_id` then equals `previous_owner_id`, and
 * `root_delegations` is empty because it was not read. The result must not say the node now
 * belongs to them, or that nothing on the node gives root.
 */
export function transferChangedOwner(result: Pick<NodeTransferResult, 'owner_id' | 'previous_owner_id'>): boolean {
  return !result.previous_owner_id || result.previous_owner_id !== result.owner_id
}

/** The typed address when it can be sent (it has an `@`), else null. The router checks the rest. */
export function transferEmail(input: string): string | null {
  const email = input.trim()
  return email.includes('@') && !/\s/.test(email) ? email : null
}

/** How a member is named in the picker and the result: the name, else the email. */
export function memberLabel(member: Pick<TransferMember, 'name' | 'email'>): string {
  // `userText`, the app's one person label (app#401): a name of '' or spaces is no name.
  return userText(member)
}

/**
 * Who the node can go to: every member of its org except its current owner, by label.
 * ⚠ Any role: the router takes a member at any role (§ 5 Node).
 */
export function transferCandidates(
  members: readonly TransferMember[],
  currentOwnerId: string | null | undefined
): TransferMember[] {
  return members
    .filter(m => !!m.userId && m.userId !== currentOwnerId)
    .sort((a, b) => memberLabel(a).localeCompare(memberLabel(b)))
}

/**
 * One line per root delegation the new owner inherits.
 *
 * ⚠⚠ THE LIST IS THE POINT OF THE RESULT (§ 5 Node: "so none is taken over silently"), so nothing
 * in it is dropped: a kind this app does not know yet is shown with what it carries. The router
 * returns `root_entry` (the access list) and `root_terminal` (router PR #1830 renamed it from
 * `elevated_terminal`, which is still read here for a router before that rename).
 */
export function rootDelegationLines(delegations: readonly RootDelegation[] | null | undefined): string[] {
  return (delegations ?? []).map(d => {
    const label = d.title || d.name || d.email || d.user_id || d.service_id || ''
    switch (d.kind) {
      case 'root_terminal':
      case 'elevated_terminal':
        return `Terminal ${label ? `"${label}" ` : ''}runs as root on this node.`
      case 'root_entry': {
        const until = d.expires_at ? ` until ${d.expires_at}` : ''
        const blocked = d.blocked_by_node ? ' The node does not allow root now, so this gives only use.' : ''
        return `${label || 'A member'} holds root on this node's access list${until}.${blocked}`
      }
      default:
        return `${d.kind}${label ? `: ${label}` : ''}`
    }
  })
}

/** A router tool error as `callTool` throws it, read by shape. */
type ToolFailure = { code?: string; message?: string } | null | undefined

/** The sentence a reader who does not control the node gets. */
export const NODE_TRANSFER_FORBIDDEN =
  'Only the owner of this node, or an owner or admin of its organization, can give it to another member.'

/** One sentence for a refused `node_transfer`. */
export function nodeTransferRefusal(error: unknown): string {
  const failure = error as ToolFailure
  switch (failure?.code) {
    case 'forbidden':
      return NODE_TRANSFER_FORBIDDEN
    case 'invalid_args':
      // ⚠ The router gives ONE answer for an unknown address, a person of another org and a person
      // who left (D-16). This sentence must not guess which.
      return 'No member of this node’s organization has that email address. Check the address, or ask an owner or admin of the organization.'
    case 'failed_precondition':
      return 'The node, its owner or that member changed while this ran. Nothing was changed. Try again.'
    case 'not_found':
      return 'This node no longer exists.'
    case 'unauthenticated':
    case 'unauthorized':
      return 'Your session has ended. Sign in again, then try once more.'
    default:
      return failure?.message || 'The node was not given to that member.'
  }
}
